import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { ref, toValue, type MaybeRefOrGetter, type Ref } from "vue";
import { cnmcApi } from "./cnmcApi";
import type {
  CnmcApplication,
  CnmcApplicationAction,
  CnmcFilingView,
} from "./cnmcTypes";

/**
 * Runs `CnmcApplicationCard` actions for the owner of the filings: an issuance plan, or a transfer or
 * export prepared from holdings. Receipt entry and replacement answer with the owner's view, which
 * goes to `update`; checks and staff resolution answer with nothing to show, so `refresh` reloads the
 * owner after them. `busy` is shared with the caller's own operations when passed; a failure goes to
 * the global error handler.
 */
export function useCnmcApplicationActions(options: {
  companyId: MaybeRefOrGetter<number>;
  connectionId: MaybeRefOrGetter<number>;
  update: (view: CnmcFilingView) => void;
  refresh: () => Promise<unknown>;
  busy?: Ref<boolean>;
}) {
  const { globalErrorHandler } = useGlobalHandlers();
  const busy = options.busy ?? ref(false);

  /** Runs one operation while `busy`, handing any failure to the global error handler. */
  const perform = async (operation: () => Promise<unknown>) => {
    busy.value = true;
    try {
      await operation();
    } catch (error) {
      globalErrorHandler(error);
    } finally {
      busy.value = false;
    }
  };

  const applicationAction = (
    application: CnmcApplication,
    action: CnmcApplicationAction
  ) =>
    perform(async () => {
      const args = [
        toValue(options.companyId),
        toValue(options.connectionId),
        application.id,
      ] as const;
      switch (action.type) {
        case "submit":
          return options.update(
            (await cnmcApi.submit(...args, action.receipt, action.reason)).data
          );
        case "replace":
          return options.update(
            (await cnmcApi.replace(...args, action.reason)).data
          );
        case "download":
          return cnmcApi.download(...args);
        case "check":
          await cnmcApi.check(...args);
          return options.refresh();
        case "resolve":
          await cnmcApi.resolve(...args, action.reason);
          return options.refresh();
      }
    });

  return { busy, perform, applicationAction };
}
